import { useMounted } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useCartStore } from '~/stores/cart.store'
import type { CartKey } from '~/stores/cart.store'
import type { Image } from '~/types/global.types'

export interface CartProduct {
    id: number
    name: string
    slug: string
    price: number | string
    max_cart_quantity: number | string | null
    images: Image[]
}

export const useCart = () => {
    const cartStore = useCartStore()
    const { cart, isHydrated, totalItems } = storeToRefs(cartStore)
    const isMounted = useMounted()

    const isReady = computed(() => isHydrated.value && isMounted.value)
    const safeTotalItems = computed(() =>
        isReady.value ? totalItems.value : 0
    )

    const isInCart = (slug: CartKey | null | undefined) => {
        if (!isReady.value) {
            return false
        }

        return cartStore.isInCart(slug)
    }

    const getItemCount = (slug: CartKey | null | undefined) => {
        if (!isReady.value) {
            return 0
        }

        return cartStore.getItemCount(slug)
    }

    const addToCart = cartStore.addToCart
    const setCount = cartStore.setCount
    const increment = cartStore.increment
    const decrement = cartStore.decrement
    const removeFromCart = cartStore.removeFromCart
    const clearCart = cartStore.clearCart
    const enforceProductLimits = cartStore.enforceProductLimits

    return {
        cart,
        isHydrated,
        isReady,
        totalItems: safeTotalItems,
        isInCart,
        getItemCount,
        addToCart,
        setCount,
        increment,
        decrement,
        removeFromCart,
        clearCart,
        enforceProductLimits,
    }
}
